import { useEffect, useState } from "react";
import { BellRing, Loader2, RefreshCcw, Save } from "lucide-react";
import { toast } from "react-toastify";
import { orderNotificationPolicyApi } from "../../api/orderNotificationPolicyApi";

const OPTIONS = [
  {
    key: "receivedEnabled",
    label: "Order received",
    description:
      "Customers are told that their order was received but is not yet confirmed.",
  },
  {
    key: "acceptedEnabled",
    label: "Order confirmed",
    description: "Send when the business accepts an order.",
  },
  {
    key: "rejectedEnabled",
    label: "Order rejected",
    description: "Send when the business rejects an order.",
  },
  {
    key: "inProgressEnabled",
    label: "In progress",
    description: "Send when processing starts.",
  },
  {
    key: "readyEnabled",
    label: "Ready",
    description: "Send when an order is marked ready.",
  },
  {
    key: "completedEnabled",
    label: "Completed",
    description: "Send when an order is completed.",
  },
  {
    key: "cancelledEnabled",
    label: "Cancelled",
    description: "Send when an order is cancelled.",
  },
];

function readError(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

export default function OrderCustomerUpdatesPage() {
  const [policy, setPolicy] = useState(null);
  const [savedPolicy, setSavedPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await orderNotificationPolicyApi.get();
      setPolicy(data);
      setSavedPolicy(data);
    } catch (requestError) {
      setError(readError(requestError, "Customer update settings could not be loaded."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const changed =
    policy && savedPolicy && JSON.stringify(policy) !== JSON.stringify(savedPolicy);

  async function save() {
    if (!policy || saving) return;
    setSaving(true);
    try {
      const data = await orderNotificationPolicyApi.update(policy);
      setPolicy(data);
      setSavedPolicy(data);
      toast.success("Order customer updates saved.");
    } catch (requestError) {
      toast.error(readError(requestError, "Customer update settings could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <header className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700">
          <BellRing size={20} aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Order customer updates</h1>
          <p className="mt-1 text-sm text-slate-600">
            Choose which committed order status changes send a WhatsApp update.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="flex min-h-48 items-center justify-center text-slate-500" role="status">
          <Loader2 className="mr-2 animate-spin" size={18} /> Loading settings
        </div>
      ) : error ? (
        <div className="border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <p>{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-3 inline-flex items-center gap-2 font-medium text-rose-800 hover:underline"
          >
            <RefreshCcw size={15} /> Retry
          </button>
        </div>
      ) : (
        <section className="border border-slate-200 bg-white">
          <div className="divide-y divide-slate-200">
            {OPTIONS.map(option => (
              <label
                key={option.key}
                className="flex cursor-pointer items-start justify-between gap-6 px-4 py-4 sm:px-5"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900">
                    {option.label}
                  </span>
                  <span className="mt-1 block text-sm text-slate-600">
                    {option.description}
                  </span>
                </span>
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 shrink-0 accent-emerald-600"
                  checked={Boolean(policy?.[option.key])}
                  onChange={event =>
                    setPolicy(current => ({
                      ...current,
                      [option.key]: event.target.checked,
                    }))
                  }
                />
              </label>
            ))}
          </div>
          <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
            <button
              type="button"
              onClick={save}
              disabled={!changed || saving}
              className="inline-flex min-h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 className="animate-spin" size={17} /> : <Save size={17} />}
              Save changes
            </button>
          </div>
        </section>
      )}
    </main>
  );
}
